import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, ChevronDown, ChevronUp, Loader2, Tag, Gift, Truck } from 'lucide-react';
import { memberApi } from './memberApi';

const SHIPPING_LABELS = {
  standard: 'Standard Express Post',
  express_on: 'Ontario Express Post',
  pickup: 'Local Pickup',
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const OrdersTab = ({ user }) => {
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    memberApi.get('/member/orders').then(r => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  if (orders === null) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#C41E3A]" size={28} /></div>;
  }

  if (orders.length === 0) {
    return (
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-10 text-center" data-testid="orders-empty">
        <ShoppingBag className="mx-auto text-gray-600 mb-4" size={36} />
        <h3 className="font-['Oswald'] text-lg uppercase tracking-wide text-white mb-2">No Orders Yet</h3>
        <p className="text-gray-500 text-sm mb-1">Orders placed with <span className="text-[#D4AF37]">{user.email}</span> will appear here.</p>
        <p className="text-gray-600 text-xs mb-6">Placed an order with a different email? It won't show under this account.</p>
        <Link to="/#products" className="inline-block bg-[#C41E3A] hover:bg-[#A01830] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors" data-testid="orders-shop-link">
          Shop Peptides
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="orders-tab">
      <p className="text-gray-500 text-xs">{orders.length} order{orders.length !== 1 ? 's' : ''} found for <span className="text-[#D4AF37]">{user.email}</span></p>
      {orders.map((o) => {
        const isOpen = open === o.order_number;
        return (
          <div key={o.order_number} className="bg-[#0A0A0A] border border-[#27272A] rounded-sm overflow-hidden" data-testid={`order-card-${o.order_number}`}>
            <button
              onClick={() => setOpen(isOpen ? null : o.order_number)}
              className="w-full flex items-center justify-between gap-3 p-4 md:p-5 text-left hover:bg-[#121212] transition-colors"
              data-testid={`order-toggle-${o.order_number}`}
            >
              <div className="min-w-0">
                <p className="font-['Oswald'] text-white text-sm md:text-base uppercase tracking-wide truncate">{o.order_number}</p>
                <p className="text-gray-500 text-xs mt-0.5">{fmtDate(o.created_at)} · {(o.items || []).reduce((s, i) => s + (i.qty || 1), 0)} item(s)</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-[#D4AF37] font-bold text-sm md:text-base">{money(o.total)}</span>
                {isOpen ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-[#27272A] p-4 md:p-5" data-testid={`order-details-${o.order_number}`}>
                <div className="space-y-2 mb-4">
                  {(o.items || []).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-gray-300 min-w-0 truncate">
                        {item.name} {item.dosage && <span className="text-gray-500 text-xs">({item.dosage})</span>}
                        <span className="text-gray-500 text-xs"> × {item.qty}</span>
                      </span>
                      <span className="text-gray-400 flex-shrink-0">{item.price}</span>
                    </div>
                  ))}
                  {o.free_item && (
                    <div className="flex items-center gap-2 text-sm text-green-400">
                      <Gift size={13} /> {o.free_item.name || 'Free item'} <span className="text-xs">— FREE</span>
                    </div>
                  )}
                </div>
                <div className="border-t border-[#27272A] pt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between text-gray-400"><span>Subtotal</span><span>{money(o.subtotal)}</span></div>
                  {Number(o.discount) > 0 && (
                    <div className="flex justify-between text-green-400">
                      <span className="flex items-center gap-1.5"><Tag size={12} /> {o.promo_code ? `${o.promo_code} discount` : 'Discount'}</span>
                      <span>-{money(o.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-400">
                    <span className="flex items-center gap-1.5"><Truck size={12} /> {SHIPPING_LABELS[o.shipping_method] || 'Shipping'}</span>
                    <span>{Number(o.shipping) === 0 ? 'FREE' : money(o.shipping)}</span>
                  </div>
                  <div className="flex justify-between text-white font-bold pt-1.5 border-t border-[#27272A]">
                    <span className="uppercase tracking-wide">Total</span>
                    <span className="text-[#D4AF37]">{money(o.total)}</span>
                  </div>
                </div>
                {(o.city || o.province) && (
                  <p className="text-gray-600 text-xs mt-3">Shipped to: {[o.city, o.province].filter(Boolean).join(', ')}</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
